<template>
  <div
    :class="[
      {
        'ps-left-0': !attachRight,
        'ps-right-0': attachRight,
        'ps-is-minimized': isMinimized || isPeek
      }
    ]"
    @transitionend="handleTransitionEnd"
    class="ps-base-ticket ps-absolute ps-bottom-0 ps-h-full ps-w-full ps-py-12 ps-pt-4 sm:ps-px-8 ps-pointer-auto"
  >
    <card-core
      :active-route="activeRoute"
      :validations="$v"
      :is-minimized="isMinimized"
      :is-create-mode="isCreateMode"
      :is-ready="isReady"
      :is-error="isError"
      :is-saving="isSaving"
      :labels="labels"
      :type-is-sub-task="typeIsSubTask"
      :browsers="browsers"
      :pages="pages"
      :current-page="currentPage"
      :comments="comments"
      :dot-id="id"
      :core="activeDot.core"
      @card-save="save"
      @card-cancel="cancel"
      @card-minimize="updateMinimize"
      @meta-add="addMetaItem"
      @comment-submit="submitComment"
      @notation-change="setNotation"
    ></card-core>
  </div>
</template>

<script>
import { validationMixin } from 'vuelidate'
import { required, requiredIf, minLength } from 'vuelidate/lib/validators'
import cloneDeep from 'lodash.clonedeep'
import {
  mapState,
  mapGetters,
  mapActions,
  mapMutations
} from '@picnic-software/vuex'
import {
  CARD_CREATE,
  ROUTE_CONTROLS,
  ROUTE_CARD,
  ROUTE_CARD_PEEK
} from '@/common/constants'

import CardCore from '@/components/card/CardCore'

export default {
  name: 'CardDetails',
  components: {
    CardCore
  },
  mixins: [validationMixin],
  data() {
    return {
      id: null,
      card: {
        list_id: null,
        priority_id: null,
        type_id: null,
        assigned_to_user_id: null
      },
      comment: {
        id: null,
        text: ''
      },
      comments: [],
      pages: [],
      browsers: [],
      isReady: false,
      isError: false,
      isSaving: false,
      isMinimized: false
    }
  },
  validations() {
    return {
      card: {
        // Core validations
        id: {},
        name: {
          required,
          minLength: minLength(3)
        },
        desc: {},
        list_id: {},
        created_at: {},
        updated_at: {},
        created_by_user: {},
        assigned_to_user_id: {},
        assigned_to_user: {},

        // pull in provider custom field validations
        ...this.fieldValidations
      },
      comment: {
        id: {},
        text: {
          required
        }
      }
    }
  },
  computed: {
    ...mapState({
      activeRoute: state => state.router.active,
      routerCardId: state => state.router.cardId,
      attachRight: state => state.router.attachRight,
      labels: state => state.provider.labels,
      currentPage: state => state.pages.active,
      activeDot: state => state.dots.active,
      fresh: state => state.dots.fresh,
      currentUser: state => state.auth.user
    }),
    ...mapGetters({
      fieldValidations: 'provider/fields/validations',
      routerMatches: 'router/matches'
    }),
    isCreateMode() {
      return this.routerCardId === CARD_CREATE
    },
    typeIsSubTask() {
      if (this.card && this.card._type) {
        const type = this.$store.getters.intTicketTypeById(this.card._type)
        return type && type.subtask
      }
    },
    isFresh() {
      return this.fresh && this.fresh === this.routerCardId
    },
    isPeek() {
      return this.activeRoute === ROUTE_CARD_PEEK
    }
  },
  watch: {
    routerCardId(val) {
      if (val) return this.loadCard()
    },

    isFresh(val) {
      if (val) {
        this.fetchCard()
        this.clearFresh()
      }
    }
  },
  mounted() {
    if (this.routerCardId) {
      this.loadCard()
    }
  },
  methods: {
    ...mapActions({
      getCard: 'dots/get',
      createCard: 'dots/create',
      createComment: 'provider/comment/create',
      updateCard: 'dots/update',
      resetCard: 'dots/active/reset',
      routerControls: 'router/controls',
      findCoreServices: 'provider/findCoreServices',
      findFieldServices: 'provider/fields/findServices',
      assignBrowser: 'browser/assign'
    }),
    ...mapMutations({
      routerTo: 'router/to',
      clearFresh: 'dots/fresh',
      assignSystem: 'dots/active/system/assign',
      setNotation: 'dots/active/core/notation'
    }),
    async setupRequireData() {
      await this.findCoreServices()
      await this.findFieldServices()
    },
    async loadCard() {
      this.isReady = false
      this.clearFresh()
      await this.setupRequireData()
      await this.fetchCard()
      this.$v.$reset()
      await this.$nextTick()
      this.isReady = true
    },
    async fetchCard() {
      const {
        id,
        card,
        pages,
        browsers,
        comments,
        created_by_user
      } = await this.getCard(this.routerCardId)
      console.log('card FETCHED', id, card, pages, browsers, comments)
      this.id = id
      this.card = card
      if (!this.card || !this.card.id) {
        this.isError = true
        return
      }
      this.pages = pages || []
      this.browsers = browsers || []
      this.comments = comments || []
    },
    async save() {
      this.isSaving = true
      this.routerTo(ROUTE_CONTROLS)
      try {
        await this.createOrUpdate()
        this.cancel()
      } catch (err) {
        console.log(err)
        this.isSaving = false
        this.routerTo('ROUTE_CARD')
      }
    },

    createOrUpdate() {
      if (this.isCreateMode) return this.create()
      return this.update()
    },
    create() {
      return this.createCard(this.card)
    },
    update() {
      const payload = {}
      Object.keys(this.card).forEach(key => {
        if (this.$v.card[key] && this.$v.card[key].$dirty) {
          payload[key] = this.card[key]
        }
      })
      return this.updateCard({
        id: this.id,
        payload: {
          card: payload
        }
      })
    },
    cancel() {
      this.resetCard()
      this.resetLocal()
      this.routerControls()
    },
    updateMinimize() {
      this.isMinimized = !this.isMinimized
    },
    resetLocal() {
      // Reset local state
      Object.assign(this.$data, this.$options.data.apply(this))
    },
    async addPage() {
      await this.updateCard({
        id: this.id,
        payload: {
          page: this.activeDot.page
        }
      })
    },
    async addBrowser() {
      await this.assignBrowser()
      this.assignSystem()
      await this.updateCard({
        id: this.id,
        payload: {
          browser: this.activeDot.browser,
          system: this.activeDot.system
        }
      })
    },
    async addMetaItem(metaType) {
      if (this.isSaving) return
      this.isSaving = true
      switch (metaType) {
        case 'page':
          await this.addPage()
          break
        case 'browser':
          await this.addBrowser()
          break
      }
      await this.$nextTick()
      this.isSaving = false
    },
    handleTransitionEnd() {
      console.log('transitionend')
    },
    async submitComment() {
      console.log('save comment', this.comment)
      if (!this.comment && this.comment.length) {
        this.isSaving = false
        return
      }
      this.isSaving = true
      // this.comment.id = 'COMMENT_PLACEHOLDER'
      this.comment.card_id = this.card.id
      delete this.comment.id
      await this.createComment(this.comment)
      // this.comment.created_by_user = this.detach(this.currentUser)
      // this.comments.push(this.detach(this.comment))

      this.comment.id = null
      this.comment.text = ''
      await this.$nextTick()
      this.isSaving = false
    }
  }
}
</script>

<style lang="postcss" scoped>
.ps-base-ticket {
  transition: all 450ms ease;
  will-change: transform;
  transform: translateY(calc(100% + 0.5rem));
  max-width: 440px;
  max-height: 700px;
  opacity: 1;
  &.ps-is-active {
    transform: translateY(0);
  }

  &.ps-is-minimized {
    transform: translateY(calc(100% - 6rem));
  }
}
</style>
